// Sidebar (DESIGN §2 + §7): wordmark · person switcher · "Search… ⌘K" · WORKSPACE (with Clients) / TEAM / SYSTEM groups.
// ≥1100px full (232px); 768–1099px a 64px icon rail (labels hidden via .nav-label); hidden below 768px (BottomNav).
import markChrome from '../assets/mark-chrome.png';
import wordmarkWhite from '../assets/wordmark-white.png';
import wordmarkBlack from '../assets/wordmark-black.png';
import './sidebar.css';
import { href } from '../router.js';
import { Icon } from '../icons.jsx';
import { SEARCH_SHORTCUT, openSearch } from './search.jsx';
import { PersonSwitcher } from './person-switcher.jsx';
import { cx } from './ui.jsx';
import { workspaceFilterPref } from '../store.js';
import { WORKSPACE_FILTERS } from '../../shared/derive.js';

export const NAV_ITEMS = {
  overview: { label: 'Overview', icon: 'overview', path: '/', routes: ['overview'] },
  workspace: { label: 'Workspace', icon: 'workspace', path: '/workspace', routes: ['workspace'] },
  clients: { label: 'Clients', icon: 'clients', path: '/clients', routes: ['clients', 'client', 'batch'] },
  calendar: { label: 'Calendar', icon: 'calendar', path: '/calendar', routes: ['calendar'] },
  performance: { label: 'Performance', icon: 'performance', path: '/performance', routes: ['performance'] },
  people: { label: 'People', icon: 'people', path: '/people', routes: ['people', 'person'] },
  settings: { label: 'Settings', icon: 'settings', path: '/settings', routes: ['settings'] },
};

export const NAV_GROUPS = [
  { label: 'Workspace', items: ['overview', 'workspace', 'clients', 'calendar', 'performance'] },
  { label: 'Team', items: ['people'] },
  { label: 'System', items: ['settings'] },
];

export const BOTTOM_NAV = ['overview', 'workspace', 'clients', 'calendar', 'performance'];

export const isNavActive = (key, route) => NAV_ITEMS[key].routes.includes(route.name);

// Brand marks from Logo/: the chrome "A" (rail, mobile top bar) and the FRAZIER wordmark
// (white letters on the dark theme, black on the light theme; the chrome A is kept as-is).
export function LogoMark({ size = 28 }) {
  return <img class="logo-mark" src={markChrome} width={size} height={size} alt="" aria-hidden="true" />;
}

export function Wordmark({ height = 22, class: cls }) {
  return (
    <span class={cx('wordmark-logo', cls)} aria-hidden="true">
      <img class="wordmark-img for-dark" src={wordmarkWhite} height={height} alt="" />
      <img class="wordmark-img for-light" src={wordmarkBlack} height={height} alt="" />
    </span>
  );
}

// Workspace reopens the filter last picked on this device (e.g. My Work).
function pathOf(id) {
  const filter = id === 'workspace' ? workspaceFilterPref() : '';
  return WORKSPACE_FILTERS.includes(filter) && filter !== 'all' ? `${NAV_ITEMS[id].path}?filter=${filter}` : NAV_ITEMS[id].path;
}

export function NavLink({ id, route, class: cls, iconSize = 18, showLabel = true }) {
  const item = NAV_ITEMS[id];
  const active = isNavActive(id, route);
  return (
    <a class={cx(cls, active && 'is-active')} href={href(pathOf(id))} aria-current={active ? 'page' : undefined} title={item.label}>
      <Icon name={item.icon} size={iconSize} />
      {showLabel && <span class="nav-label">{item.label}</span>}
    </a>
  );
}

export function Sidebar({ route }) {
  return (
    <aside class="sidebar" aria-label="Primary">
      <a class="sidebar-brand" href={href('/')} aria-label="FRAZIER — Overview">
        <LogoMark size={28} />
        <Wordmark height={22} class="nav-label" />
      </a>
      <PersonSwitcher />
      <button type="button" class="sidebar-search" onClick={() => openSearch()} title={`Search (${SEARCH_SHORTCUT})`} aria-label="Search">
        <Icon name="search" size={16} />
        <span class="sidebar-search-text nav-label">Search…</span>
        <span class="kbd nav-label" aria-hidden="true">{SEARCH_SHORTCUT}</span>
      </button>
      <nav class="nav" aria-label="Main">
        {NAV_GROUPS.map((group) => (
          <div class="nav-group" key={group.label} role="group" aria-label={group.label}>
            <div class="nav-group-label nav-label" aria-hidden="true">{group.label}</div>
            {group.items.map((id) => <NavLink key={id} id={id} route={route} class="nav-item" />)}
          </div>
        ))}
      </nav>
    </aside>
  );
}

// Below 768px: 5 items (Overview, Workspace, Clients, Calendar, Performance) — fits 375px. People/Settings live in the avatar menu.
export function BottomNav({ route }) {
  return (
    <nav class="bottom-nav" aria-label="Main">
      {BOTTOM_NAV.map((id) => <NavLink key={id} id={id} route={route} class="bottom-nav-item" iconSize={20} />)}
    </nav>
  );
}
